/**
 * Sends problem reports and automatic telemetry to the game server (`POST /api/report`, shared/feedback.ts).
 *
 * Offline-first: a report that cannot be delivered (no server, signed out of a dead connection, a 5xx / 429) is
 * kept in a small localStorage queue and sent when the server can be reached again (on the next flush: every
 * minute, on `online`, on reconnect). A report the server refuses as malformed (4xx) is dropped, never retried.
 * The queue is also what "Save as file" exports. Storage may be unavailable (private mode): then it is memory only.
 */
import { REPORT_LIMITS, type ReportPayload } from '../../shared/feedback';

export const QUEUE_KEY = 'bh.feedback.queue';
/** At most this many reports wait; the oldest automatic ones go first when it is full. */
export const QUEUE_MAX = 12;

export interface QueuedReport {
  payload: ReportPayload;
  /** When it was queued (epoch ms). */
  queuedAt: number;
}

export type SendOutcome = 'sent' | 'queued' | 'dropped';

export interface ReporterDeps {
  /** '' = same origin (production, Vite proxy in dev). */
  base?: () => string;
  /** The signed-in session's bearer token, if any (ties the report to the account). */
  token?: () => string | null;
  fetchImpl?: typeof fetch;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  now?: () => number;
}

function defaultStorage(): ReporterDeps['storage'] {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

/** Keep the body under the server's cap: trim log lines (oldest first) until it fits. */
export function fitPayload(p: ReportPayload): ReportPayload {
  const out: ReportPayload = { ...p, message: p.message.slice(0, REPORT_LIMITS.message) };
  if (out.stack) out.stack = out.stack.slice(0, REPORT_LIMITS.stack);
  if (out.context?.log) out.context = { ...out.context, log: out.context.log.slice(-REPORT_LIMITS.logLines).map((l) => l.slice(0, REPORT_LIMITS.logLine)) };
  while (JSON.stringify(out).length > REPORT_LIMITS.body - 512 && out.context?.log?.length) out.context = { ...out.context, log: out.context.log.slice(1) };
  return out;
}

export class Reporter {
  private memory: QueuedReport[] = [];
  private flushing: Promise<void> | null = null;
  private readonly fetchImpl: typeof fetch;
  private readonly storage: ReporterDeps['storage'];
  private readonly now: () => number;
  /** Results of the last sends (newest last), for the hooks and tests. */
  readonly history: { at: number; kind: string; outcome: SendOutcome; status: number }[] = [];

  constructor(private readonly deps: ReporterDeps = {}) {
    this.fetchImpl = deps.fetchImpl ?? ((...a) => fetch(...a));
    this.storage = deps.storage === undefined ? defaultStorage() : deps.storage;
    this.now = deps.now ?? Date.now;
    this.memory = this.load();
  }

  /** Reports waiting to be delivered (oldest first). */
  get queue(): QueuedReport[] {
    return this.memory.slice();
  }

  /** Try to deliver now; on failure keep it for later. */
  async send(payload: ReportPayload): Promise<SendOutcome> {
    const p = fitPayload(payload);
    const r = await this.post(p);
    if (r === 'retry') {
      this.enqueue(p);
      return this.note(p, 'queued', 0);
    }
    return this.note(p, r.ok ? 'sent' : 'dropped', r.status);
  }

  /** Deliver what is queued (one at a time, stops at the first that still can't go). */
  flush(): Promise<void> {
    if (this.flushing) return this.flushing;
    this.flushing = (async () => {
      try {
        while (this.memory.length) {
          const head = this.memory[0]!;
          const r = await this.post(head.payload);
          if (r === 'retry') return;
          this.memory.shift();
          this.save();
          this.note(head.payload, r.ok ? 'sent' : 'dropped', r.status);
        }
      } finally {
        this.flushing = null;
      }
    })();
    return this.flushing;
  }

  /** Everything queued, as a JSON document the player can save and send another way. */
  exportJson(extra?: ReportPayload): string {
    const items = [...this.memory.map((q) => q.payload), ...(extra ? [fitPayload(extra)] : [])];
    return JSON.stringify({ game: 'Borough Heat', exportedAt: new Date(this.now()).toISOString(), reports: items }, null, 2);
  }

  clearQueue(): void {
    this.memory = [];
    this.save();
  }

  private note(p: ReportPayload, outcome: SendOutcome, status: number): SendOutcome {
    this.history.push({ at: this.now(), kind: p.kind, outcome, status });
    if (this.history.length > 30) this.history.shift();
    return outcome;
  }

  private async post(p: ReportPayload): Promise<{ ok: boolean; status: number } | 'retry'> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const token = this.deps.token?.();
    if (token) headers.authorization = `Bearer ${token}`;
    let res: Response;
    try {
      res = await this.fetchImpl(`${this.deps.base?.() ?? ''}/api/report`, { method: 'POST', headers, body: JSON.stringify(p), keepalive: true });
    } catch {
      return 'retry'; // no server (offline play, the box is down): later
    }
    if (res.ok) return { ok: true, status: res.status };
    // Not there (static hosting without a game server behind it), busy, rate limited, or down: later.
    if (res.status === 404 || res.status === 429 || res.status >= 500) return 'retry';
    return { ok: false, status: res.status }; // refused as malformed: retrying would not help
  }

  private enqueue(p: ReportPayload): void {
    this.memory.push({ payload: p, queuedAt: this.now() });
    while (this.memory.length > QUEUE_MAX) {
      const auto = this.memory.findIndex((q) => q.payload.kind !== 'report');
      this.memory.splice(auto >= 0 ? auto : 0, 1);
    }
    this.save();
  }

  private load(): QueuedReport[] {
    try {
      const raw = this.storage?.getItem(QUEUE_KEY);
      const v = raw ? (JSON.parse(raw) as unknown) : [];
      return Array.isArray(v) ? (v.filter((q) => q && typeof q === 'object' && (q as QueuedReport).payload) as QueuedReport[]).slice(-QUEUE_MAX) : [];
    } catch {
      return [];
    }
  }

  private save(): void {
    try {
      if (this.memory.length) this.storage?.setItem(QUEUE_KEY, JSON.stringify(this.memory));
      else this.storage?.removeItem(QUEUE_KEY);
    } catch {
      /* quota / disabled: memory only */
    }
  }
}
